"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { DayPicker, type DateRange } from "react-day-picker";
import { GREEK_CITIES_SORTED, GREEK_CITY_REGIONS } from "@/lib/greek-cities";
import { formatDate, formatDateRange } from "@/lib/format";

/**
 * Mobile-only sticky bar for /search. Matches Airbnb's compact
 * search-results header: a left back arrow, a centre pill showing
 * the current search summary (title + meta line), and a right
 * filter icon. Tapping the pill opens a full-screen search modal
 * (Where / When / Who); tapping the filter icon opens the existing
 * Filters modal via onToggleFilters.
 *
 * Hidden on desktop via CSS — SearchSummaryBar handles desktop.
 */
const FEATURED_DESTS: { city: string; emoji: string }[] = [
  { city: "Athens", emoji: "🏛" },
  { city: "Mykonos", emoji: "⛵" },
  { city: "Santorini", emoji: "🌅" },
  { city: "Crete", emoji: "🏖" },
  { city: "Thessaloniki", emoji: "🏙" },
  { city: "Naxos", emoji: "🏝" },
  { city: "Corfu", emoji: "🌊" },
  { city: "Rhodes", emoji: "🏰" },
];

type Card = "where" | "when" | "who";

type Props = {
  initialCity: string;
  initialCheckIn: string;
  initialCheckOut: string;
  initialAdults: string;
  initialChildren: string;
  initialInfants: string;
  initialPets: string;
  extraParams: Record<string, string | string[]>;
  filterCount: number;
  onToggleFilters?: () => void;
};

async function locateAndRedirect(
  router: ReturnType<typeof useRouter>,
  onError: (msg: string) => void,
) {
  if (!("geolocation" in navigator)) {
    onError("Your browser doesn't support geolocation.");
    return;
  }
  if ("permissions" in navigator) {
    try {
      const status = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      if (status.state === "denied") {
        onError("Location is blocked. Allow it in browser settings.");
        return;
      }
    } catch {
      // fall through
    }
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const params = new URLSearchParams();
      params.set("lat", pos.coords.latitude.toFixed(5));
      params.set("lng", pos.coords.longitude.toFixed(5));
      router.push(`/search?${params.toString()}`);
    },
    () => onError("Couldn't get your location — pick a city instead."),
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
  );
}

export default function SearchSummaryMobile({
  initialCity,
  initialCheckIn,
  initialCheckOut,
  initialAdults,
  initialChildren,
  initialInfants,
  initialPets,
  extraParams,
  filterCount,
  onToggleFilters,
}: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Card>("where");

  const [city, setCity] = useState(initialCity);
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(initialCheckOut);
  const initAdults = Math.max(1, parseInt(initialAdults || "0", 10) || 0);
  const [adults, setAdults] = useState(initAdults || 1);
  const [children, setChildren] = useState(parseInt(initialChildren || "0", 10) || 0);
  const [infants, setInfants] = useState(parseInt(initialInfants || "0", 10) || 0);
  const [pets, setPets] = useState(parseInt(initialPets || "0", 10) || 0);
  const [locateError, setLocateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totalAdultChild = adults + children;

  // Title + meta shown inside the centre pill of the mobile bar.
  const pillTitle = city ? `Stays in ${city}` : "All stays in Greece";
  const pillMeta = (() => {
    const parts: string[] = [];
    if (checkIn && checkOut) {
      const fmt = (s: string) =>
        new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      parts.push(`${fmt(checkIn)} — ${fmt(checkOut)}`);
    } else {
      parts.push("Add dates");
    }
    if (totalAdultChild > 0) {
      parts.push(`${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}`);
    } else {
      parts.push("Add guests");
    }
    return parts.join(" · ");
  })();

  function clearAll() {
    setCity("");
    setCheckIn("");
    setCheckOut("");
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setPets(0);
    setActive("where");
  }

  function submit() {
    const u = new URLSearchParams();
    for (const [k, v] of Object.entries(extraParams)) {
      if (Array.isArray(v)) v.forEach((x) => x && u.append(k, x));
      else if (v) u.set(k, v);
    }
    if (city) u.set("city", city);
    else u.delete("city");
    if (checkIn) u.set("checkIn", checkIn);
    if (checkOut) u.set("checkOut", checkOut);
    const g = adults + children;
    if (g > 0) u.set("guests", String(g));
    if (adults > 0) u.set("adults", String(adults));
    if (children > 0) u.set("children", String(children));
    if (infants > 0) u.set("infants", String(infants));
    if (pets > 0) u.set("pets", String(pets));
    u.delete("page");
    setOpen(false);
    router.push(`/search?${u.toString()}`);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const range: DateRange | undefined = (() => {
    const f = checkIn ? new Date(checkIn) : undefined;
    const t = checkOut ? new Date(checkOut) : undefined;
    if (!f && !t) return undefined;
    return { from: f, to: t };
  })();

  return (
    <>
      <div className="srm-bar">
        <button
          type="button"
          aria-label="Back"
          className="srm-icon-btn"
          onClick={() => router.back()}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8}>
            <path d="M13 4l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          className="srm-pill"
          onClick={() => {
            setOpen(true);
            setActive("where");
          }}
        >
          <span className="srm-pill-title">{pillTitle}</span>
          <span className="srm-pill-meta">{pillMeta}</span>
        </button>

        <button
          type="button"
          aria-label="Filters"
          className="srm-icon-btn"
          onClick={onToggleFilters}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6}>
            <path d="M4 5h12M6 10h8M8 15h4" strokeLinecap="round" />
            <circle cx="14" cy="5" r="1.5" fill="var(--surface)" />
            <circle cx="8" cy="10" r="1.5" fill="var(--surface)" />
            <circle cx="12" cy="15" r="1.5" fill="var(--surface)" />
          </svg>
          {filterCount > 0 ? <span className="srm-icon-badge">{filterCount}</span> : null}
        </button>
      </div>

      {open ? (
        <div className="search-mobile-modal" role="dialog" aria-modal="true">
          <header className="smm-head">
            <button
              type="button"
              className="smm-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>

          <div className="smm-body">
            <section
              className={`smm-card${active === "where" ? " on" : ""}`}
              onClick={() => active !== "where" && setActive("where")}
            >
              {active === "where" ? (
                <>
                  <h2 className="smm-card-title">Where?</h2>
                  <div className="smm-input-wrap">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
                      <circle cx={6} cy={6} r={4} />
                      <path d="M9 9l4 4" />
                    </svg>
                    <input
                      autoFocus
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Search destinations"
                      autoComplete="off"
                      spellCheck={false}
                      className="smm-input"
                    />
                    {city ? (
                      <button
                        type="button"
                        aria-label="Clear"
                        className="smm-input-clear"
                        onClick={() => setCity("")}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  <div className="smm-suggest-header">Suggested destinations</div>
                  {(() => {
                    const q = city.trim().toLowerCase();
                    const list = q
                      ? GREEK_CITIES_SORTED.filter((c) => c.toLowerCase().includes(q)).slice(0, 8)
                      : null;
                    if (list == null) {
                      return (
                        <>
                          <button
                            type="button"
                            className="sb-suggest-row"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocateError(null);
                              locateAndRedirect(router, setLocateError);
                            }}
                          >
                            <span className="sb-suggest-thumb sb-suggest-thumb-nearby">
                              <svg width="18" height="18" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                <path d="M12 2L2 6.5l4.5 1.5L8 12z" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="sb-suggest-text">
                              <span className="sb-suggest-main">Nearby</span>
                              <span className="sb-suggest-sub">Find what&apos;s around you</span>
                            </span>
                          </button>
                          {FEATURED_DESTS.map((d) => {
                            const region = GREEK_CITY_REGIONS[d.city];
                            return (
                              <button
                                key={d.city}
                                type="button"
                                className="sb-suggest-row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCity(d.city);
                                  setActive("when");
                                }}
                              >
                                <span className="sb-suggest-thumb sb-suggest-thumb-emoji">{d.emoji}</span>
                                <span className="sb-suggest-text">
                                  <span className="sb-suggest-main">{d.city}, Greece</span>
                                  <span className="sb-suggest-sub">
                                    {region ? `For ${region.toLowerCase()}-region stays` : "Greece"}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </>
                      );
                    }
                    if (list.length === 0) {
                      return (
                        <div className="sb-suggest-empty">
                          No matches — tap Search anyway for &ldquo;{city}&rdquo;.
                        </div>
                      );
                    }
                    return list.map((c) => {
                      const region = GREEK_CITY_REGIONS[c];
                      return (
                        <button
                          key={c}
                          type="button"
                          className="sb-suggest-row"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCity(c);
                            setActive("when");
                          }}
                        >
                          <span className="sb-suggest-thumb sb-suggest-thumb-pin">
                            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                              <path d="M7 13s-4-3.5-4-7a4 4 0 018 0c0 3.5-4 7-4 7z" />
                              <circle cx="7" cy="6" r="1.5" />
                            </svg>
                          </span>
                          <span className="sb-suggest-text">
                            <span className="sb-suggest-main">{c}, Greece</span>
                            <span className="sb-suggest-sub">{region ?? "Greece"}</span>
                          </span>
                        </button>
                      );
                    });
                  })()}
                  {locateError ? (
                    <p
                      style={{
                        fontSize: 12.5,
                        color: "var(--warn)",
                        padding: "10px 4px 0",
                      }}
                    >
                      {locateError}
                    </p>
                  ) : null}
                </>
              ) : (
                <CollapsedRow label="Where" value={city || "I'm flexible"} />
              )}
            </section>

            <section
              className={`smm-card${active === "when" ? " on" : ""}`}
              onClick={() => active !== "when" && setActive("when")}
            >
              {active === "when" ? (
                <>
                  <h2 className="smm-card-title">When?</h2>
                  <div className="smm-cal">
                    <DayPicker
                      mode="range"
                      selected={range}
                      onSelect={(r) => {
                        if (!r) {
                          setCheckIn("");
                          setCheckOut("");
                          return;
                        }
                        const hadComplete = Boolean(checkIn && checkOut);
                        if (hadComplete && r.from && r.to) {
                          const stateDates = new Set([checkIn, checkOut]);
                          const fromStr = formatDate(r.from);
                          const toStr = formatDate(r.to);
                          const clicked = !stateDates.has(fromStr)
                            ? r.from
                            : !stateDates.has(toStr)
                              ? r.to
                              : null;
                          if (clicked) {
                            setCheckIn(formatDate(clicked));
                            setCheckOut("");
                            return;
                          }
                        }
                        if (r.from && r.to && r.to.getTime() === r.from.getTime()) {
                          setCheckIn(formatDate(r.from));
                          setCheckOut("");
                          return;
                        }
                        if (r.from && r.to && r.to < r.from) {
                          setCheckIn(formatDate(r.to));
                          setCheckOut(formatDate(r.from));
                          return;
                        }
                        setCheckIn(r.from ? formatDate(r.from) : "");
                        setCheckOut(r.to ? formatDate(r.to) : "");
                        if (r.from && r.to) setActive("who");
                      }}
                      disabled={[{ before: today }]}
                      numberOfMonths={1}
                      weekStartsOn={1}
                    />
                  </div>
                </>
              ) : (
                <CollapsedRow
                  label="When"
                  value={
                    checkIn && checkOut
                      ? formatDateRange(checkIn, checkOut)
                      : "Add dates"
                  }
                />
              )}
            </section>

            <section
              className={`smm-card${active === "who" ? " on" : ""}`}
              onClick={() => active !== "who" && setActive("who")}
            >
              {active === "who" ? (
                <>
                  <h2 className="smm-card-title">Who?</h2>
                  <div
                    className="sb-who"
                    style={{
                      position: "static",
                      boxShadow: "none",
                      padding: 0,
                      border: 0,
                      width: "auto",
                      marginTop: 4,
                    }}
                  >
                    <StepperRow title="Adults" sub="Ages 13 or above" value={adults} min={1} onChange={setAdults} />
                    <StepperRow title="Children" sub="Ages 2 — 12" value={children} min={0} onChange={setChildren} />
                    <StepperRow title="Infants" sub="Under 2" value={infants} min={0} onChange={setInfants} />
                    <StepperRow title="Pets" sub="Check the listing's pet policy" value={pets} min={0} onChange={setPets} last />
                  </div>
                </>
              ) : (
                <CollapsedRow
                  label="Who"
                  value={
                    totalAdultChild === 0
                      ? "Add guests"
                      : `${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}${
                          infants > 0 ? `, ${infants} infant${infants === 1 ? "" : "s"}` : ""
                        }${pets > 0 ? `, ${pets} pet${pets === 1 ? "" : "s"}` : ""}`
                  }
                />
              )}
            </section>
          </div>

          <footer className="smm-foot">
            <button type="button" className="smm-clear" onClick={clearAll}>
              Clear all
            </button>
            <button type="button" className="search-btn smm-search" onClick={submit}>
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx={6} cy={6} r={4} />
                <path d="M9 9l4 4" />
              </svg>
              <span>Search</span>
            </button>
          </footer>
        </div>
      ) : null}
    </>
  );
}

function CollapsedRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="smm-collapsed">
      <span className="smm-collapsed-label">{label}</span>
      <span className="smm-collapsed-value">{value}</span>
    </div>
  );
}

function StepperRow({
  title,
  sub,
  value,
  min,
  onChange,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div className={`sb-who-row${last ? " last" : ""}`}>
      <div>
        <div className="sb-who-title">{title}</div>
        <div className="sb-who-sub">{sub}</div>
      </div>
      <div className="sb-who-stepper" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={`Decrease ${title}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span aria-live="polite">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${title}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
